import type { CraftingGrid, ItemId, RecipeType } from '@/types/domain';

export interface NormalizedRecipe {
  shapedKey: string;
  shapelessKey: string;
  type: RecipeType;
  ingredientIds: ItemId[];
  grid: CraftingGrid;
  boundingBox: { rows: number; cols: number };
}

const EMPTY = '';

function cellKey(cell: { itemId: ItemId; count?: number } | null): string {
  if (!cell) return EMPTY;
  const count = cell.count && cell.count > 1 ? `x${cell.count}` : '';
  return `${cell.itemId}${count}`;
}

/** Expand a 9-cell grid into a 3x3 matrix of item-id strings ("" for empty). */
function toMatrix(grid: CraftingGrid): string[][] {
  const m: string[][] = [[], [], []];
  for (let i = 0; i < 9; i++) {
    const r = Math.floor(i / 3);
    const c = i % 3;
    m[r][c] = cellKey(grid[i]);
  }
  return m;
}

/** Trim empty rows/cols, returning the minimal bounding box content. */
function trimMatrix(m: string[][]): { box: string[][]; rows: number; cols: number } {
  let minR = 3, maxR = -1, minC = 3, maxC = -1;
  for (let r = 0; r < 3; r++) {
    for (let c = 0; c < 3; c++) {
      if (m[r][c] !== EMPTY) {
        if (r < minR) minR = r;
        if (r > maxR) maxR = r;
        if (c < minC) minC = c;
        if (c > maxC) maxC = c;
      }
    }
  }
  if (maxR < 0) return { box: [], rows: 0, cols: 0 };
  const rows = maxR - minR + 1;
  const cols = maxC - minC + 1;
  const box: string[][] = [];
  for (let r = minR; r <= maxR; r++) {
    const row: string[] = [];
    for (let c = minC; c <= maxC; c++) row.push(m[r][c]);
    box.push(row);
  }
  return { box, rows, cols };
}

function isShapelessLooking(m: string[][]): boolean {
  // If all filled cells share the same single item id (pure material pile), or the
  // arrangement is a single item, treat defaults lean toward shapeless — but we keep
  // shaped as the deterministic default; this heuristic only informs "auto" mode.
  let filled = 0;
  let firstId: string | null = null;
  for (let r = 0; r < 3; r++)
    for (let c = 0; c < 3; c++) {
      const v = m[r][c];
      if (v === EMPTY) continue;
      filled++;
      if (firstId === null) firstId = v;
      else if (v !== firstId) return false;
    }
  return filled > 0 && firstId !== null;
}

export function normalize(grid: CraftingGrid, type: RecipeType = 'shaped'): NormalizedRecipe {
  const m = toMatrix(grid);
  const { box, rows, cols } = trimMatrix(m);

  // shaped key: positions preserved & trimmed
  const shapedKey = box.map((row) => row.join(',')).join(';');

  // ingredient multiset
  const ingredientIds: ItemId[] = [];
  for (let i = 0; i < 9; i++) {
    const cell = grid[i];
    if (!cell) continue;
    const count = cell.count && cell.count > 1 ? cell.count : 1;
    for (let k = 0; k < count; k++) ingredientIds.push(cell.itemId);
  }

  // shapeless key: sorted multiset
  const shapelessKey = ingredientIds.slice().sort().join(',');

  return {
    shapedKey,
    shapelessKey,
    type,
    ingredientIds,
    grid,
    boundingBox: { rows, cols },
  };
}

/** Choose the deterministic cache key for the chosen interpretation. */
export function makeKey(norm: NormalizedRecipe, type: RecipeType = norm.type): string {
  return type === 'shapeless' ? `sl:${norm.shapelessKey}` : `sh:${norm.shapedKey}`;
}

/** Heuristic: a single distinct item anywhere usually means shapeless. */
export function suggestType(grid: CraftingGrid): RecipeType {
  const m = toMatrix(grid);
  return isShapelessLooking(m) ? 'shapeless' : 'shaped';
}
